import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { integrationService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plug, Loader2, Plus, Lock, X } from "lucide-react";
import { ConnectorIcon, useTenantScope, TenantPicker, SelectTenantNotice, PageHeader } from "./shared";

function AddDialog({ connector, scope, onClose, onCreated }) {
  const [name, setName] = useState(`${connector.name}`);
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (scope.isSuper && !scope.tenantId) return toast.error("Select a tenant first");
    setSaving(true);
    try {
      const r = await integrationService.create({
        name: name.trim(), connectorCode: connector.connectorCode,
        tenantId: scope.tenantId || undefined,
      });
      toast.success("Integration created");
      onCreated(r);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-integration-dialog">
      <div className="bg-card border border-border rounded-xl p-5 w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-foreground flex items-center gap-2"><ConnectorIcon code={connector.connectorCode} className="h-5 w-5 text-primary" /> New {connector.name}</h3>
          <button onClick={onClose} data-testid="add-dialog-close"><X className="h-4 w-4 text-muted-foreground" /></button>
        </div>
        <label className="text-xs text-muted-foreground">Integration name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground mt-1 mb-4" data-testid="add-integration-name" autoFocus />
        <button onClick={create} disabled={saving} className="w-full inline-flex items-center justify-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="add-integration-submit">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create integration
        </button>
      </div>
    </div>
  );
}

export default function ConnectorCatalog() {
  const scope = useTenantScope();
  const { isSuper, tenantId } = scope;
  const navigate = useNavigate();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    integrationService.connectors().then((d) => setConnectors(d.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="connector-catalog-page">
      <PageHeader icon={Plug} title="Connectors"
        subtitle="Pick a connector to create a tenant integration. ERP & Database connectors are coming soon.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="connector-grid">
          {connectors.map((c) => (
            <div key={c.connectorCode} className={`bg-card border rounded-xl p-5 flex flex-col gap-3 ${c.available ? "border-border hover:border-primary/40" : "border-border opacity-75"} transition-colors`} data-testid={`connector-${c.connectorCode}`}>
              <div className="flex items-start justify-between">
                <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><ConnectorIcon code={c.connectorCode} className="h-5 w-5" /></span>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/40 border border-border rounded-full px-2 py-0.5">{c.category}</span>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground">{c.name}</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed min-h-[2.5rem]">{c.description}</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {(c.supportedTargets || []).map((t) => <span key={t} className="text-[10px] text-muted-foreground bg-background border border-border rounded px-1.5 py-0.5">{t}</span>)}
              </div>
              {c.available ? (
                <button onClick={() => setDialog(c)} className="mt-auto inline-flex items-center justify-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-xs font-semibold hover:bg-primary/90" data-testid={`connector-add-${c.connectorCode}`}>
                  <Plus className="h-3.5 w-3.5" /> Add integration
                </button>
              ) : (
                <button disabled className="mt-auto inline-flex items-center justify-center gap-1.5 border border-border rounded-lg px-3 py-2 text-xs font-semibold text-muted-foreground cursor-not-allowed" data-testid={`connector-soon-${c.connectorCode}`}>
                  <Lock className="h-3.5 w-3.5" /> Coming soon
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {dialog && <AddDialog connector={dialog} scope={scope} onClose={() => setDialog(null)} onCreated={(r) => navigate(`/admin/integrations/${r.id}${tenantId ? `?tenantId=${tenantId}` : ""}`)} />}
    </div>
  );
}
